import { z } from 'zod';

import { RmfErrorCode } from './rmf.js';
import { CreativeErrorCode } from './creative.js';
import { IntegrationErrorCode, type ConnectorCapabilities } from './integrations.js';

/**
 * ADIM 4 — KAMPANYA OLUŞTURMA SİHİRBAZININ SÖZLEŞMESİ.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Sihirbaz YENİ bir uç açmıyor — VAR OLAN dört ucu SIRAYLA çağırıyor
 * ═══════════════════════════════════════════════════════════════════════════
 *     1. POST /rmf/campaigns        → kampanya   (PAUSED doğar)
 *     2. POST /rmf/adsets           → ad set     (PAUSED doğar)
 *     3. POST /creative/:id/publish → reklam     (kütüphanedeki kreatifle)
 *     4. POST /campaigns/status     → yayına alma (AYRI onay, para harcatır)
 *
 * Dördü de bugün canlı. Bu dosyanın işi, o dört çağrının arasındaki KARARLARI
 * tek bir yerde tutmak — hangi hedef hangi optimizasyonla gider, hangi alan
 * hangi platformda pratikte zorunludur, hata hangi ekrana dallanır.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * 🔴 REKLAM `POST /rmf/ads` İLE AÇILMAZ
 * ───────────────────────────────────────────────────────────────────────────
 * Sözleşmede reklam oluşturmanın İKİ yolu var ve ikisi de gerçek bir `Ad`
 * üretiyor:
 *
 *   (a) `POST /rmf/ads` — `creative` alanı yalnız METİN taşıyor (`assetUrl`
 *       bilerek yok, `rmfAdCreativeSchema` başlığı). Meta tarafında bu yol
 *       `image_hash`siz `link_data` gönderiyor; Meta'nın bunu kabul edip
 *       etmediği sunucudan HİÇ DOĞRULANMADI (bugüne kadar 0 kampanya açıldı).
 *   (b) `POST /creative/:id/publish` — kütüphanedeki GERÇEK görsel/videoyla,
 *       imzalama, `Ad.creativeId` bağı ve kredi muhasebesi dahil.
 *
 * Sihirbaz (b)'yi yürüyor. İkisi de çağrılırsa AYNI ad set altında İKİ reklam
 * doğar ve kullanıcı ikisini de öder. `/rmf/ads` bu akışta HİÇ çağrılmıyor;
 * onun yeri, platformda ZATEN duran bir kreatifi bağlamak
 * (`existingCreativeRef`) — sihirbazın sormadığı bir soru.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * 🔴 KATALOGLAR ELLE YAZILDI — TÜRETİLMEDİ
 * ───────────────────────────────────────────────────────────────────────────
 * `createCampaignSchema.objective` serbest bir dize (1..64) ve normalize
 * EDİLMEMESİ bilinçli bir karar. Yani seçilebilir hedeflerin listesi
 * sözleşmede YOK, hiçbir uçta yayımlı DEĞİL ve `packages/shared`ta bir
 * karşılığı yoktu. Sihirbaz kullanıcıya serbest metin kutusu sunamaz —
 * `OUTCOME_TRAFFIC` yazmasını beklemek ürün değil.
 *
 * Bu yüzden aşağıdaki üç katalog (hedef, optimizasyon hedefi, faturalama
 * olayı) BU DOSYADA ELLE YAZILDI. Bunlar bir sözleşmenin kopyası değil,
 * BAĞIMSIZ bir bilgi kaynağı ve bu ayrımın yazılı olması şart: sözleşmeden
 * türetilebilecek bir liste elle yazılırsa bir gün sessizce ayrışır.
 * Doğrulayan tek merci PLATFORMUN KENDİSİ — yanlış bir üçlü gönderilirse
 * adapter değil Meta reddediyor ve gerekçe `providerMessage` ile geliyor
 * (`meta.connector.ts` `createAdSet` başlığı: "optimization_goal,
 * billing_event ve promoted_object ÜÇÜ BİRDEN tutarlı olmak zorunda").
 *
 * Katalog bu yüzden DAR tutuldu: her satırı doğrulanabilir, yaygın ve
 * tehlikesiz olanlar. Eksik bir hedefe ihtiyaç duyulursa buraya EKLENİR;
 * kullanıcıya serbest metin AÇILMAZ.
 *
 * ⚠️ Normalize etme İDDİASI YOK. Değerler platformun KENDİ sözcükleri
 * (`OUTCOME_TRAFFIC`, `SEARCH`) ve platformlar arasında hiçbir eşleme
 * kurulmuyor — Meta'nın `OUTCOME_TRAFFIC`i ile Google'ın `SEARCH`i aynı
 * satırda değil, aynı listede bile değil.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Yetenek kapısı — sihirbaz hangi hesapta AÇILIR
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `ConnectorCapabilities.canCreateEntities` ile `available` AYRI iki soru ve
 * arayüz onlara AYRI cevap veriyor (`RmfErrorCode.PLATFORM_CANNOT_CREATE`
 * yorumu):
 *
 *   · `available: false`          → kart "yakında" gösterilir.
 *   · `canCreateEntities: false`  → "Kampanya oluştur" düğmesi HİÇ GÖSTERİLMEZ.
 *
 * 22 Ağu 2026 değerleri: META `true`, GOOGLE `true`, LINKEDIN `true`,
 * TIKTOK `false`, TWITTER `false`. Yani bugün sihirbaz yalnız Meta hesabında
 * gerçekten çalışabiliyor (Google'ın adapter'ı var ama bağlı hesap yok).
 *
 * 🔴 BU BAYRAK KODA GÖMÜLMEZ. Kaynak `GET /integrations/platforms` yanıtındaki
 * `PlatformCard.capabilities`. Elle bir platform listesi yazmak, adapter
 * yazıldığı gün ekranın yalan söylemesi demek.
 */
export function canPlatformCreate(capabilities: ConnectorCapabilities | undefined): boolean {
  return Boolean(capabilities?.available && capabilities.canCreateEntities);
}

// ═══════════════════════════════════════════════════════════════════════════
// Adımlar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sihirbazın adımları — akışın TANIMI burada, bileşende değil.
 *
 * ⚠️ Adım sırası dolu daire içinde bir RAKAM ROZETİ olarak basılmaz; sessiz
 * bir üst etiket olarak yazılır ("ADIM 2/5"). Bu bir tasarım kararı ve
 * projede daha önce verildi.
 *
 * `launch` neden ayrı bir adım: oluşturulan her şey `PAUSED` doğuyor
 * (`rmf.ts` başlığı) ve yayına almak AYRI bir uç, AYRI bir `Idempotency-Key`,
 * AYRI bir denetim satırı. Onu son adımın içine gizlemek, "kaydet"e basan
 * kullanıcının farkında olmadan para harcamaya başlaması demekti.
 */
export const WIZARD_STEPS = [
  /// Hesap seçimi. `canCreateEntities: false` olan platformların hesapları
  /// listede GÖRÜNÜR ama seçilemez ve sebebi yanında yazar — hiç göstermemek
  /// "hesabım nerede" sorusunu doğurur.
  'account',
  /// Kampanya: ad, hedef, bütçe, özel reklam kategorisi.
  'campaign',
  /// Ad set: ad, bütçe, optimizasyon, hedefleme, tarih aralığı.
  'adset',
  /// Kreatif seçimi + reklam metni + hedef adres + marka kimliği.
  'creative',
  /// Özet ve yayına alma onayı. Burada "atla" da meşru bir seçim:
  /// duraklatılmış kampanya panelde durur ve sonra yayına alınabilir.
  'launch',
] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

/**
 * Bir turda yayınlanabilecek en fazla kreatif.
 *
 * Tavan var çünkü her seçim GERÇEK bir reklam açıyor ve kredi harcıyor:
 * ızgarada yanlışlıkla "tümünü seç"e benzeyen bir hareket, kullanıcıya
 * ödemediği bir faturayı ödetirdi. On, bir ad set altında elle yönetilebilir
 * kreatif sayısının üst sınırı — üstü zaten A/B testi işi, sihirbazın değil.
 */
export const WIZARD_MAX_CREATIVES = 10;

/**
 * `?adIds=a,b,c` — adres çubuğundaki çoklu reklam kimliği.
 *
 * Ham `z.array(z.uuid())` KULLANILAMAZ: adresten gelen şey her zaman tek bir
 * DİZE ve dizi şeması onu reddederdi (`uuidListParam`in başındaki tuzağın
 * aynısı, farklı ayraçla). Bölme burada yapılıyor ki adresi okuyan tek yer
 * bu şema olsun.
 */
const wizardAdIdsParam = z
  .string()
  .transform((value) =>
    value
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0),
  )
  .pipe(z.array(z.uuid()).min(1).max(WIZARD_MAX_CREATIVES));

/**
 * Sihirbazın ADRESTE tutulan durumu.
 *
 * 🔴 BU ALANLAR OLMADAN SİHİRBAZ KURTARILAMAZ. Oluşturulan kampanya ve ad set
 * GERÇEK kayıtlar (platformda gerçek nesneler) ama "hangi adımdayım" bilgisi
 * hiçbir yerde saklanmıyordu. Kullanıcı sekmeyi kapatıp dönerse ya baştan
 * başlar — ki bu İKİNCİ BİR KAMPANYA açar — ya da elle bulmak zorunda kalır.
 *
 * `localStorage` yerine adres seçildi: paylaşılabilir, geri tuşuyla çalışır,
 * cihaz başına değil oturum başına doğru, ve şema değişikliği istemiyor.
 * İkinci adım patlarsa (yetim, duraklatılmış kampanya) kullanıcı aynı
 * adresle DEVAM EDER, baştan başlamaz.
 */
export const wizardStateSchema = z.object({
  step: z.enum(WIZARD_STEPS).default('account'),
  /// BİZİM `AdAccount.id`imiz.
  adAccountId: z.uuid().optional(),
  /// Oluşturulmuş kampanyanın BİZİM kimliğimiz. Dolu ise 1. çağrı BİTTİ.
  campaignId: z.uuid().optional(),
  /// Oluşturulmuş ad set. Dolu ise 2. çağrı BİTTİ.
  adSetId: z.uuid().optional(),
  /**
   * Yayınlanmış reklamlar. Dolu ise 3. çağrı BİTTİ, geriye yalnız yayına alma
   * kaldı.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * 🔴 ÇOĞUL — tekil `adId` sessizce reklam DÜŞÜRÜYORDU
   * ═══════════════════════════════════════════════════════════════════════
   * Kreatif adımı artık kütüphaneden birden fazla seçime izin veriyor ve her
   * seçim AYNI ad set altında ayrı bir reklam açıyor. Bu uydurulmuş bir
   * yetenek değil, yayınlama sözleşmesinin açıkça tarif ettiği durum
   * (`publishSchema.identityRef`: "mevcut bir ad set'e ikinci kreatif
   * eklerken doğru davranış budur").
   *
   * Alan tekil kalsaydı yayına alma adımı yalnız İLK reklamı `ACTIVE`
   * yapardı; kalanlar `PAUSED` durur, hiçbir ekran bunu söylemez ve kullanıcı
   * kredisini ödediği kreatiflerin neden yayında olmadığını anlayamazdı.
   *
   * Adreste virgülle ayrılmış duruyor (`?adIds=a,b,c`). Tekrar eden anahtar
   * (`?adIds=a&adIds=b`) DEĞİL: `buildQuery` (apps/web) tek değer yazıyor ve
   * sıralı/kararlı bir adres üretmesi bilinçli.
   */
  adIds: wizardAdIdsParam.optional(),
});
export type WizardState = z.infer<typeof wizardStateSchema>;

/**
 * Adres durumundan gerçek adımı türet.
 *
 * `step` parametresine körü körüne güvenilmez: kullanıcı `?step=creative`
 * yazıp kampanyasız gelebilir. Tamamlanmış çağrılar tek gerçek — onlardan
 * geri kalan ilk adım seçilir.
 */
export function resolveWizardStep(state: WizardState): WizardStep {
  if (!state.adAccountId) return 'account';
  if (!state.campaignId) return 'campaign';
  if (!state.adSetId) return 'adset';
  if (!state.adIds || state.adIds.length === 0) return 'creative';
  return 'launch';
}

// ═══════════════════════════════════════════════════════════════════════════
// Hedef kataloğu — ELLE YAZILDI (bkz. dosya başlığı)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bir hedefin sunduğu optimizasyon/faturalama çifti.
 *
 * `optimizationGoal` ve `billingEvent` `createAdSetSchema`da isteğe bağlı ama
 * META'DA ZORUNLU (servis platforma göre karar veriyor) ve GOOGLE'da
 * gönderilirse `FIELD_NOT_SUPPORTED_ON_PLATFORM` ile REDDEDİLİYOR. Yani aynı
 * iki alan bir platformda zorunlu, diğerinde yasak — bu yüzden katalog
 * platform kırılımlı.
 */
export interface OptimizationOption {
  /// Platformun kendi sözcüğü. `createAdSetSchema.optimizationGoal`a bu gider.
  optimizationGoal: string;
  /// Platformun kendi sözcüğü. `createAdSetSchema.billingEvent`e bu gider.
  billingEvent: string;
  /// i18n anahtarı — düz metin DEĞİL. Ekran çevirir, sunucu değil.
  labelKey: string;
  /**
   * Bu seçim `promotedObject` (piksel + olay) İSTİYOR mu.
   *
   * `OFFSITE_CONVERSIONS` piksel olmadan Meta tarafından reddediliyor.
   * Adapter bunu doğrulamıyor — reddi platform veriyor. Arayüz alanı zorunlu
   * kılarak kullanıcıyı o reddin önünde durduruyor.
   */
  requiresPromotedObject: boolean;
}

export interface CampaignObjectiveOption {
  /// Platformun KENDİ hedef sözcüğü. `createCampaignSchema.objective`e AYNEN
  /// gider; normalize edilmez, çevrilmez.
  value: string;
  /// i18n anahtarı (`campaignWizard.objective.<value>`).
  labelKey: string;
  /// i18n anahtarı — bir cümlelik açıklama.
  descriptionKey: string;
  /// Bu hedefte sunulacak optimizasyon/faturalama çiftleri. Google'da BOŞ:
  /// o platformda bu iki alan gönderilemez.
  optimizations: readonly OptimizationOption[];
}

/**
 * META hedefleri (ODAX sözcükleri).
 *
 * Beş hedef seçildi; Meta'nın tamamı değil. `OUTCOME_APP_PROMOTION` DIŞARIDA
 * çünkü uygulama tanıtımı `promoted_object.application_id` istiyor ve
 * `rmfTargetingSchema`nın ne o alanı ne de mağaza seçimini karşılığı var —
 * listeye koymak, son adımda platformun reddedeceği bir yol açmak olurdu.
 */
const META_OBJECTIVES: readonly CampaignObjectiveOption[] = [
  {
    value: 'OUTCOME_TRAFFIC',
    labelKey: 'campaignWizard.objective.OUTCOME_TRAFFIC',
    descriptionKey: 'campaignWizard.objectiveHint.OUTCOME_TRAFFIC',
    optimizations: [
      {
        optimizationGoal: 'LINK_CLICKS',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.LINK_CLICKS',
        requiresPromotedObject: false,
      },
      {
        optimizationGoal: 'LANDING_PAGE_VIEWS',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.LANDING_PAGE_VIEWS',
        requiresPromotedObject: false,
      },
    ],
  },
  {
    value: 'OUTCOME_AWARENESS',
    labelKey: 'campaignWizard.objective.OUTCOME_AWARENESS',
    descriptionKey: 'campaignWizard.objectiveHint.OUTCOME_AWARENESS',
    optimizations: [
      {
        optimizationGoal: 'REACH',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.REACH',
        requiresPromotedObject: false,
      },
      {
        optimizationGoal: 'IMPRESSIONS',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.IMPRESSIONS',
        requiresPromotedObject: false,
      },
    ],
  },
  {
    value: 'OUTCOME_ENGAGEMENT',
    labelKey: 'campaignWizard.objective.OUTCOME_ENGAGEMENT',
    descriptionKey: 'campaignWizard.objectiveHint.OUTCOME_ENGAGEMENT',
    optimizations: [
      {
        optimizationGoal: 'POST_ENGAGEMENT',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.POST_ENGAGEMENT',
        requiresPromotedObject: false,
      },
    ],
  },
  {
    value: 'OUTCOME_LEADS',
    labelKey: 'campaignWizard.objective.OUTCOME_LEADS',
    descriptionKey: 'campaignWizard.objectiveHint.OUTCOME_LEADS',
    optimizations: [
      {
        optimizationGoal: 'OFFSITE_CONVERSIONS',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.OFFSITE_CONVERSIONS',
        requiresPromotedObject: true,
      },
    ],
  },
  {
    value: 'OUTCOME_SALES',
    labelKey: 'campaignWizard.objective.OUTCOME_SALES',
    descriptionKey: 'campaignWizard.objectiveHint.OUTCOME_SALES',
    optimizations: [
      {
        optimizationGoal: 'OFFSITE_CONVERSIONS',
        billingEvent: 'IMPRESSIONS',
        labelKey: 'campaignWizard.optimization.OFFSITE_CONVERSIONS',
        requiresPromotedObject: true,
      },
    ],
  },
];

/**
 * GOOGLE — TEK seçenek ve bu bir eksiklik değil, ADAPTER'IN SÖZLEŞMESİ.
 *
 * `google.create.ts` içindeki `CREATABLE_CHANNEL_TYPES` tablosunda tek bir
 * satır var: `SEARCH`. Başka bir değer gönderilirse adapter, platforma hiç
 * gitmeden "Google'da bu uçtan yalnız arama (SEARCH) kampanyası
 * oluşturulabiliyor" diye reddediyor. Yani liste burada UYDURULMADI, adapter
 * tablosundan OKUNDU ve tek satır olması onun tek satır olmasından.
 *
 * Optimizasyon listesi BOŞ ve bu da bilinçli: Google'da bu iki alan
 * gönderilemez (`FIELD_NOT_SUPPORTED_ON_PLATFORM`).
 */
const GOOGLE_OBJECTIVES: readonly CampaignObjectiveOption[] = [
  {
    value: 'SEARCH',
    labelKey: 'campaignWizard.objective.SEARCH',
    descriptionKey: 'campaignWizard.objectiveHint.SEARCH',
    optimizations: [],
  },
];

/**
 * Platform → hedef kataloğu.
 *
 * ⚠️ `AdPlatform`ın TAMAMI DEĞİL. TIKTOK ve TWITTER `canCreateEntities: false`
 * (sihirbaz orada hiç açılmıyor), LINKEDIN `true` ama hedef sözcüklerinin
 * hiçbiri sunucudan doğrulanmadı ve uydurulmuş bir liste, kullanıcının son
 * adımda platform reddi almasıyla biter. LinkedIn hesabı bağlandığında bu
 * listeye eklenecek; o güne kadar `wizardObjectives` boş dizi döndürür ve
 * arayüz "bu platform için hedef listesi henüz tanımlı değil" der —
 * SESSİZCE boş bir açılır kutu göstermez.
 */
export const CAMPAIGN_OBJECTIVES: Readonly<Record<string, readonly CampaignObjectiveOption[]>> = {
  META: META_OBJECTIVES,
  GOOGLE: GOOGLE_OBJECTIVES,
};

export function wizardObjectives(platform: string): readonly CampaignObjectiveOption[] {
  return CAMPAIGN_OBJECTIVES[platform] ?? [];
}

export function wizardOptimizations(
  platform: string,
  objective: string,
): readonly OptimizationOption[] {
  return wizardObjectives(platform).find((o) => o.value === objective)?.optimizations ?? [];
}

// ═══════════════════════════════════════════════════════════════════════════
// Özel reklam kategorileri (Meta)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `createCampaignSchema.specialAdCategories` seçenekleri.
 *
 * 🔴 YANLIŞ BİLDİRMEK HESABIN KAPANMASINA YOL AÇABİLİYOR. Bu yüzden alan
 * "isteğe bağlı bir etiket" gibi sunulmaz: ekranda ne olduğunu açıklayan bir
 * not ile birlikte ve hiçbiri seçilmediğinde AÇIKÇA "hiçbiri" seçili görünür.
 * Boş dizi = hiçbiri (`meta.connector.ts`: boş dizi olsa bile gönderilmek
 * zorunda). Google'da karşılığı yok ve adapter alanı kullanmıyor.
 *
 * Değerler Meta'nın kendi sözcükleri; ELLE yazıldı (bkz. dosya başlığı).
 */
export const SPECIAL_AD_CATEGORIES = [
  'CREDIT',
  'EMPLOYMENT',
  'HOUSING',
  'ISSUES_ELECTIONS_POLITICS',
] as const;
export type SpecialAdCategory = (typeof SPECIAL_AD_CATEGORIES)[number];

/**
 * Reklam eylem düğmesi (`publishSchema.ctaType` / `rmfAdCreativeSchema.ctaType`).
 *
 * Dar bir küme, Meta'nın tamamı değil — ELLE yazıldı. Serbest metin
 * bırakılsaydı yazım hatası platform reddiyle son adımda ortaya çıkardı.
 */
export const AD_CTA_TYPES = [
  'LEARN_MORE',
  'SHOP_NOW',
  'SIGN_UP',
  'CONTACT_US',
  'DOWNLOAD',
  'BOOK_TRAVEL',
] as const;
export type AdCtaType = (typeof AD_CTA_TYPES)[number];

// ═══════════════════════════════════════════════════════════════════════════
// Platform başına ALAN ZORUNLULUKLARI
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 SÖZLEŞME İLE ADAPTER ARASINDAKİ FARK — HİÇBİR DOKÜMANDA YAZMIYORDU.
 *
 * `createAdSetSchema` üç alanı isteğe bağlı tanımlıyor: `targeting.countries`,
 * `optimizationGoal`, `billingEvent`. Ama adapter'lar aynı fikirde değil:
 *
 *   · Meta ÜLKESİZ AD SET'İ REDDEDİYOR. `meta.connector.ts` `createAdSet`
 *     ülke listesi boşsa fırlatıyor ve gerekçesi kodda yazılı: "US gibi bir
 *     varsayılan UYDURMUYORUZ — yanlış ülkeye reklam vermek, reklam
 *     vermemekten pahalı." Yani sözleşmede isteğe bağlı olan alan Meta
 *     akışında PRATİKTE ZORUNLU.
 *   · Meta `optimization_goal` ve `billing_event` olmadan ad set açmıyor.
 *   · Google aynı iki alan gönderilirse `FIELD_NOT_SUPPORTED_ON_PLATFORM`
 *     ile REDDEDİYOR.
 *
 * Bu tabloyu arayüzün içine gömmek, üç ekranın (form doğrulama, özet, hata
 * mesajı) aynı kuralı üç kez yorumlaması demekti. Tek yerde.
 */
export interface WizardFieldRequirements {
  /// `targeting.countries` en az bir ülke istiyor mu.
  countriesRequired: boolean;
  /// `optimizationGoal` + `billingEvent` zorunlu mu.
  optimizationRequired: boolean;
  /// `optimizationGoal` + `billingEvent` GÖNDERİLEMEZ mi (gönderilirse ret).
  optimizationForbidden: boolean;
  /**
   * `identityRef` (Meta'da Sayfa kimliği) pratikte zorunlu mu.
   *
   * Şemada isteğe bağlı çünkü MEVCUT bir ad set'e ikinci reklam eklenirken
   * adapter kimliği kardeş reklamdan devralıyor. Ama sihirbaz YENİ bir ad set
   * açıyor ve o ad set'in İLK reklamında devralınacak hiçbir şey yok —
   * adapter açık bir hatayla düşüyor. Sihirbaz bu yüzden alanı zorunlu kılar.
   */
  identityRefRequired: boolean;
}

export const WIZARD_FIELD_REQUIREMENTS: Readonly<Record<string, WizardFieldRequirements>> = {
  META: {
    countriesRequired: true,
    optimizationRequired: true,
    optimizationForbidden: false,
    identityRefRequired: true,
  },
  GOOGLE: {
    countriesRequired: false,
    optimizationRequired: false,
    optimizationForbidden: true,
    identityRefRequired: false,
  },
};

/// Tanınmayan platformda en gevşek kural: hiçbir şeyi zorunlu kılma, hiçbir
/// şeyi yasaklama. Uydurma bir zorunluluk, kullanıcıyı dolduramayacağı bir
/// alanın önünde durdurur.
const PERMISSIVE_REQUIREMENTS: WizardFieldRequirements = {
  countriesRequired: false,
  optimizationRequired: false,
  optimizationForbidden: false,
  identityRefRequired: false,
};

export function wizardFieldRequirements(platform: string): WizardFieldRequirements {
  return WIZARD_FIELD_REQUIREMENTS[platform] ?? PERMISSIVE_REQUIREMENTS;
}

// ═══════════════════════════════════════════════════════════════════════════
// Marka kimliği (Meta Sayfa kimliği) — nerede saklanır
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 SAKLANACAK BİR ALAN YOK VE ŞEMA DONMUŞ.
 *
 * Meta Sayfa kimliği her yeni ad set'in ilk reklamında gerekiyor ama ne
 * `Workspace`ta ne `AdAccount`ta onu tutacak bir kolon var; sayfa listesini
 * çekmek de mümkün değil (`pages_show_list` izni OAuth kapsamlarımızda yok).
 *
 * Üç seçenek vardı: (1) her seferinde elle yazdırmak — kullanıcının
 * `1315580038304394` gibi bir numarayı ezberlemesi, (2) tarayıcıda hatırlamak,
 * (3) şemaya alan eklemek — YASAK, yönetici kararı.
 *
 * (2) seçildi ve sınırı EKRANDA YAZILI olmak zorunda: değer bu tarayıcıda
 * kalır, ekip arkadaşı aynı numarayı kendi tarayıcısına ilk kez girer. Alan
 * HER ZAMAN düzenlenebilir kalır — hatırlanan değer bir öneri, bir kilit değil.
 *
 * Anahtar hesap başına: aynı çalışma alanında iki reklam hesabı iki farklı
 * Sayfa'ya bağlı olabilir ve tek anahtar, ikincisinde yanlış sayfayı önerirdi.
 */
export function identityRefStorageKey(adAccountId: string): string {
  return `mentorivo.wizard.identityRef.${adAccountId}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// Kreatif seçici
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Seçicinin `GET /creatives` çağrısında kullanacağı süzgeç.
 *
 * 🔴 KREATİF TEK KULLANIMLIK. `creative.service.publish` yalnız
 * `status: READY` ve `s3Key` dolu kreatifi kabul ediyor; yayınlandıktan sonra
 * satır `PUBLISHED`a çekiliyor ve ikinci kez yayınlanamıyor
 * (`ALREADY_PUBLISHED`). Yani seçici `READY` süzmek ZORUNDA — süzmezse
 * kullanıcı seçebildiği bir kreatifle 409 alır.
 *
 * ⚠️ ÜRÜN BOŞLUĞU, KAPATILMADI: kullanıcı aynı görseli iki kampanyada
 * kullanmak isterse cevap yok. Bu kısıt seçicinin yanında BİR CÜMLEYLE
 * yazılır ("yayınlanan kreatif bu listeden düşer") ve stüdyodaki kopya/iyileştir
 * akışına bağlantı verilir. Sessizce listeden düşürmek, kullanıcının
 * kreatifinin silindiğini sanması demek.
 */
export const WIZARD_CREATIVE_FILTER = { status: 'READY' } as const;

// ═══════════════════════════════════════════════════════════════════════════
// Hata dallanması
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sihirbazın tanıdığı hata kodları — ÜÇ FARKLI SÖZLÜKTEN geliyorlar.
 *
 * Arayüz `code` üzerinden dallanır, `message` yalnız gösterimdir. Bu birleşik
 * liste ikinci bir sözlük DEĞİL: değerler `RmfErrorCode` ve
 * `CreativeErrorCode`tan OKUNUYOR, burada yeniden yazılmıyor. Yeniden
 * yazılsaydı bir gün biri sapar ve ekran o kodu tanımaz hâle gelirdi.
 */
export const WIZARD_ERROR_CODES = {
  ...RmfErrorCode,
  /// Kreatif hazır değil (üretim bitmemiş / dosya yok).
  SOURCE_NOT_READY: CreativeErrorCode.SOURCE_NOT_READY,
  /// Kreatif zaten yayınlanmış — seçici süzmediyse buraya düşülür.
  ALREADY_PUBLISHED: CreativeErrorCode.ALREADY_PUBLISHED,
  /**
   * 🔴 EKİP ONAYI KAPISI — SİHİRBAZIN SESSİZ KİLİDİ.
   *
   * `creative.service.publish` platform çağrısından ÖNCE
   * `approvals.assertPublishable` çağırıyor. Çalışma alanı onay istiyorsa
   * (`Workspace.creativeApprovalRequired`) yayın 403 ile engelleniyor.
   *
   * Bu dalın KENDİ SONU var ve plan onu hiç anmıyor: sihirbaz "kampanya
   * hazır" ile değil "kreatif onaya gönderildi" ile biter. Kampanya ve ad set
   * gerçekten oluştu, duraklatılmış duruyor; eksik olan yalnız reklam.
   * Kullanıcıya söylenecek şey bu — "bir hata oluştu" değil.
   */
  APPROVAL_REQUIRED: CreativeErrorCode.APPROVAL_REQUIRED,
} as const;
export type WizardErrorCode = (typeof WIZARD_ERROR_CODES)[keyof typeof WIZARD_ERROR_CODES];

/**
 * Kullanıcının bu hatadan sonra ne yapabileceği.
 *
 * Ekranın en önemli kararı bu: aynı düğme bazı kodlarda "tekrar dene",
 * bazılarında "tekrar DENEME" demek zorunda.
 */
export const WizardRecovery = {
  /// Aynı gövdeyle, AYNI anahtarla yeniden denenebilir (geçici arıza).
  RETRY: 'retry',
  /// Girdi düzeltilmeli; düzeltince YENİ anahtar üretilir.
  FIX_INPUT: 'fix_input',
  /**
   * 🔴 TEKRAR DENEME — ÖNCE PLATFORMU KONTROL ET.
   *
   * `PLATFORM_OUTCOME_UNKNOWN`: platform çağrısı zaman aşımıyla bitti, varlık
   * OLUŞMUŞ OLABİLİR. Aynı anahtarla ikinci istek çalıştırılmadan 409'a
   * düşüyor. "Tekrar dene" düğmesi göstermek, kullanıcıya ikinci bir kampanya
   * açtırma riski demek.
   */
  CHECK_PLATFORM: 'check_platform',
  /// Bağlantı onarılmalı (yeniden yetkilendirme).
  RECONNECT: 'reconnect',
  /// Bekleyip yeniden denemeli (kota).
  WAIT: 'wait',
  /// Akış burada BİTİYOR; hata değil, farklı bir son.
  ENDS_HERE: 'ends_here',
} as const;
export type WizardRecovery = (typeof WizardRecovery)[keyof typeof WizardRecovery];

export const WIZARD_ERROR_RECOVERY: Readonly<Record<string, WizardRecovery>> = {
  [RmfErrorCode.PLATFORM_CANNOT_CREATE]: WizardRecovery.ENDS_HERE,
  [RmfErrorCode.PARENT_NOT_FOUND]: WizardRecovery.FIX_INPUT,
  [RmfErrorCode.PLATFORM_AUTH_FAILED]: WizardRecovery.RECONNECT,
  [RmfErrorCode.RATE_LIMITED]: WizardRecovery.WAIT,
  [RmfErrorCode.PLATFORM_UNAVAILABLE]: WizardRecovery.RETRY,
  [RmfErrorCode.PLATFORM_REJECTED]: WizardRecovery.FIX_INPUT,
  /**
   * 🔴 Varsayılana (RETRY) DÜŞMEMELİ. `wizardRecovery` tanımadığı kodu
   * "tekrar dene" sayıyor; bu kodda aynı gövde her seferinde aynı reddi alır.
   * Kullanıcının yapması gereken değeri düzeltmek değil, alanı KALDIRMAK —
   * ama iki eylem de aynı ekranı (formu düzenle) açtığı için `FIX_INPUT`.
   */
  [RmfErrorCode.FIELD_NOT_SUPPORTED_ON_PLATFORM]: WizardRecovery.FIX_INPUT,
  [RmfErrorCode.PLATFORM_ERROR]: WizardRecovery.RETRY,
  /// 201 ile gelir, hata değil — uyarı. Akış DURUR: bir sonraki adım
  /// `campaignId` istiyor ve elimizde `id: null` var.
  [RmfErrorCode.LOCAL_RECORD_NOT_WRITTEN]: WizardRecovery.ENDS_HERE,
  [RmfErrorCode.PLATFORM_OUTCOME_UNKNOWN]: WizardRecovery.CHECK_PLATFORM,
  [CreativeErrorCode.SOURCE_NOT_READY]: WizardRecovery.FIX_INPUT,
  [CreativeErrorCode.ALREADY_PUBLISHED]: WizardRecovery.FIX_INPUT,
  [CreativeErrorCode.APPROVAL_REQUIRED]: WizardRecovery.ENDS_HERE,
  /**
   * Demo bağlantı seçilmiş. `RETRY` varsayılanı burada zararlı olurdu:
   * aynı hesapla kaç kez denenirse denensin cevap değişmez. Çözüm gerçek
   * bir reklam hesabı seçmek — yani forma dönmek.
   */
  [IntegrationErrorCode.DEMO_CONNECTION_NOT_CALLABLE]: WizardRecovery.FIX_INPUT,
};

export function wizardRecovery(code: string | undefined): WizardRecovery {
  if (!code) return WizardRecovery.RETRY;
  return WIZARD_ERROR_RECOVERY[code] ?? WizardRecovery.RETRY;
}

/**
 * Hata kodunun i18n anahtarı.
 *
 * 🔴 `t.has()` İLE KORUNMADAN BASILMAZ. Sunucu anahtar gönderir, ekran
 * çevirir; sözlükte olmayan bir kod ham dize olarak ekrana düşer.
 *
 * Sözlük durumu (29 Ağu 2026): `rmf.errors` altında EN ve TR'de 14 anahtar
 * var ve ikisi eşit. Burada eskiden `PLATFORM_OUTCOME_UNKNOWN` ve
 * `APPROVAL_REQUIRED` eksik yazıyordu — ikisi de eklendi, en son
 * `FIELD_NOT_SUPPORTED_ON_PLATFORM`. Yeni bir `RmfErrorCode` eklerken
 * anahtar İKİ sözlüğe birden girmeli; biri unutulursa o dilde ham kod düşer.
 */
export function wizardErrorKey(code: string | undefined): string {
  return `rmf.errors.${code ?? RmfErrorCode.PLATFORM_ERROR}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hız sınırı ve idempotency
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `/rmf/*` uçları DAKİKADA 10 istek kabul ediyor (durum/bütçe uçlarının
 * yarısı). Dört adımlık bir sihirbazda kullanıcı hata alıp düzeltip yeniden
 * gönderdiğinde her düzeltme YENİ bir çağrı demek — 10 gerçekçi bir tavan.
 *
 * Bunun iki sonucu var ve ikisi de arayüzün işi:
 *   1. Adım düğmesi gönderim sürerken KİLİTLENİR. Çift tıklama, aynı gövdeyle
 *      ikinci bir çağrı ve boşa harcanan bir kota demek.
 *   2. HTTP 429'un KENDİ mesajı olmalı. O yanıt throttler'dan geliyor,
 *      `RmfErrorCode.RATE_LIMITED` gövdesiyle DEĞİL — ayrı bir kod yolu.
 */
export const RMF_RATE_LIMIT_PER_MINUTE = 10;

/**
 * `Idempotency-Key` semantiği (`creative/idempotency.ts`):
 *
 *   · aynı anahtar + aynı gövde  → saklanan yanıt döner (güvenli tekrar)
 *   · aynı anahtar + FARKLI gövde → 400 `IDEMPOTENCY_KEY_REUSED`
 *   · iş hâlâ sürüyor            → 409 `IDEMPOTENCY_IN_PROGRESS`
 *
 * 🔴 ARAYÜZÜN KURALI: anahtar ADIM BAŞINA üretilir ve gövde DEĞİŞMEDİĞİ
 * sürece SABİT kalır (ağ hatasında güvenle tekrar edilebilsin); kullanıcı
 * formu düzenleyip yeniden gönderdiğinde YENİLENİR (yoksa 400 alır).
 *
 * En az 8 karakter zorunlu. `crypto.randomUUID()` her iki koşulu da sağlıyor.
 */
export const MIN_IDEMPOTENCY_KEY_LENGTH = 8;

// ═══════════════════════════════════════════════════════════════════════════
// Yayına alma onayı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Son adımın onay kartına giden özet.
 *
 * 🔴 Bu onay PARA HARCATIYOR ve metni bunu AÇIKÇA yazmak zorunda: günlük
 * tutar ve HESABIN para birimi (bugün TRY). "Yayına al" demek yetmez —
 * kullanıcının ilk gerçek denemesi 75 ₺/gün × 4 gün.
 *
 * `endTime` yoksa `null`: süresiz kampanya, kullanıcı elle durdurana kadar
 * harcar ve onay kartı bunu ayrıca söyler.
 */
export interface WizardLaunchSummary {
  campaignName: string;
  adSetName: string;
  /// Hesabın para birimi — `AdAccountRow.currency`. Uydurulmaz.
  currency: string;
  dailyBudget: number | null;
  lifetimeBudget: number | null;
  /// ISO datetime. `null` = süresiz.
  endTime: string | null;
  /// Bilinen bitiş varsa toplam tahmini harcama; hesaplanamıyorsa `null`.
  /// ⚠️ Bir GARANTİ değil bir tahmin — platform günlük bütçeyi %25'e kadar
  /// aşabiliyor ve metin bunu söylemeli.
  estimatedTotal: number | null;
}

/**
 * Yayına alma çağrısının gövdesi `entityStatusChangeSchema` — sihirbazda
 * hedef TEK kampanya, `status: 'ACTIVE'`.
 *
 * ⚠️ AD SET VE REKLAM DA DURAKLATILMIŞ DOĞUYOR. Yalnız kampanyayı ACTIVE
 * yapmak, hiyerarşinin altı duraklatılmış kaldığı için hiçbir şey
 * yayınlamaz. Onay üç hedefi birden gönderir (kampanya + ad set + reklam) ve
 * bu tek çağrıya sığar (`targets` en çok 100). Kısmi başarı yine mümkün:
 * `BulkActionResult.failed` doluysa hangi seviyenin açılamadığı SATIRA
 * SATIRA yazılır — "yayına alındı" demek, biri reddedildiyse yalan olur.
 */
export const LAUNCH_TARGET_LEVELS = ['CAMPAIGN', 'ADSET', 'AD'] as const;
